import { reactive, ref, computed, onMounted, watch } from '@vue/composition-api'
import Vue from 'vue'
import store from '@/store'
import router from '@/router'
import * as CommonInquiry from '@/logic/common/CommonInquiry'
import * as CommonEstimate from '@/logic/common/CommonEstimate'
import * as CommonNewApplication from '@/logic/common/CommonNewApplication'
import * as apiInquiry from '@/api/inquiry'
import * as common from '@/util/common'

/**
 * ラジオボタン切替用 決済方法
 */
const RADIO_PAYMENT_METHOD = Object.freeze({
  CREDIT_CARD: 1, // クレジットカード / 口座振替
  BANK_TRANSFER_WEB: 2 // 銀行振込（WEB請求書）
})

/**
 * ラジオボタン切替用 決済方法（表示名）
 */
const RADIO_PAYMENT_METHOD_NAME = Object.freeze({
  [RADIO_PAYMENT_METHOD.CREDIT_CARD]: 'クレジットカード / 口座振替',
  [RADIO_PAYMENT_METHOD.BANK_TRANSFER_WEB]: '銀行振込（WEB請求書）'
})

/**
 * 決済方法
 * 申込認証コード取得APIのpay_methodの値
 */
const PAYMENT_METHOD = Object.freeze({
  CREDIT_CARD_LUMP_SUM: 1, // クレジットカード一括払い
  CREDIT_CARD_INSTALLMENT: 2, // クレジットカード分割払い
  ACCOUNT_TRANSFER: 3, // 口座振替
  BANK_TRANSFER_WEB: 9 // 銀行振込（WEB請求書）
})

/**
 * 申込認証コード取得APIの request_status の値
 */
const OFFER_CODE_REQUEST_STATUS = Object.freeze({
  UNAPPLIED: 1, // 未申請
  APPLIED_NO_CONTRACT: 2, // 申請済みで未契約
  CONTRACTED: 3 // 契約済
})

/**
 * 決済方法（表示名）
 */
const PAYMENT_METHOD_NAME = Object.freeze({
  [PAYMENT_METHOD.CREDIT_CARD_LUMP_SUM]: 'クレジットカード一括払い',
  [PAYMENT_METHOD.CREDIT_CARD_INSTALLMENT]: 'クレジットカード分割払い',
  [PAYMENT_METHOD.ACCOUNT_TRANSFER]: '口座振替',
  [PAYMENT_METHOD.BANK_TRANSFER_WEB]: '銀行振込（WEB請求書）'
})

// 新規契約申し込み入力画面（LCログイン後）
export const getGroupIndex = () => {

  const group1 = reactive({

    // webSessionを取得したかどうか
    isWebSessionFetched: false,
    // ログイン済みかどうか
    isWebSessionValid: false,
    // 新規契約申込ボタンを押下したかどうか
    isLoginChecked: false,
    // WEB会員情報
    web_mem_info: undefined,

    // フォーム入力値
    introducer: '',
    note: '',
    productsLicenses: [], // 選択した商品＆ライセンス数のリスト

    packagesAndProducts: [], // パッケージ＆商品一覧データ

    radioPaymentMethod: RADIO_PAYMENT_METHOD.CREDIT_CARD, // 決済方法ラジオボタン切替用

    // 決済方法ラジオボタン切替用（表示名）
    radioPaymentMethodName: computed(() => {
      return RADIO_PAYMENT_METHOD_NAME[group1.radioPaymentMethod]
    }),

    offerCode: '', // 申込認証コード
    offerCodeRequestStatus: undefined, // 自動決済お申込み状態
    paymentMethod: PAYMENT_METHOD.CREDIT_CARD_LUMP_SUM, // 決済方法

    isOfferCodeUnapplied: computed(() => { // 自動決済 未申請（または値が空）かどうか
      const isEmpty = (group1.offerCodeRequestStatus === undefined)
      const isUnapplied = (group1.offerCodeRequestStatus === OFFER_CODE_REQUEST_STATUS.UNAPPLIED)
      return isEmpty || isUnapplied
    }),
    isOfferCodeAappliedNoContract: computed(() => { // 自動決済 申請済みで未契約かどうか
      return group1.offerCodeRequestStatus === OFFER_CODE_REQUEST_STATUS.APPLIED_NO_CONTRACT
    }),
    isOfferCodeContracted: computed(() => { // 自動決済 契約済かどうか
      return group1.offerCodeRequestStatus === OFFER_CODE_REQUEST_STATUS.CONTRACTED
    }),

    // 入力エラーフラグ
    isError: false,

    // フォーム入力エラーメッセージ
    error_introducer: '',
    error_note: '',
    error_productsLicenses: '',
    error_offerCode: '',

    // 送信中フラグ（二重送信防止用）
    isProcessing: false,

    // パッケージ・商品一覧ロード完了フラグ
    isPackageAndLicensesLoaded: false,

    /**
     * 初期表示
     */
    init: () => {
      group1.isLoginChecked = false
      store.dispatch('common/showLoading')
      CommonNewApplication.getWebSession(group1).finally(() => {
        store.dispatch('common/hideLoading')

        const isWebAccount = group1.isWebSessionFetched && group1.isWebSessionValid

        // 確認画面から戻ってきた場合、ストアからデータをロード
        group1.web_mem_info = store.state.newApplication.webMemInfo ?? undefined
        group1.introducer = store.state.inquiry.introducer ?? ''
        group1.note = store.state.inquiry.note ?? ''
        group1.productsLicenses = store.state.inquiry.productsLicenses ?? []
        group1.radioPaymentMethod = store.state.inquiry.radioPaymentMethod ?? RADIO_PAYMENT_METHOD.CREDIT_CARD

        watch(() => group1.introducer, () => {
          group1.updateStore()
        })
        watch(() => group1.note, () => {
          group1.updateStore()
        })

        // パッケージ・商品一覧APIをコール
        CommonNewApplication.loadPackageAndLicenses(group1).then(() => {
          group1.updateStore()

          watch(() => JSON.stringify(group1.packagesAndProducts), () => {
            group1.updateProductsLicenses()
            group1.updateStore()
          })
        })

        watch(() => group1.radioPaymentMethod, () => {
          group1.updateStore()
        })

        // 申込認証コード取得APIをコール（状態取得のみ、エラーチェックなし）
        CommonNewApplication.onGetOfferCode(group1, false)
      })
    },

    /**
     * 購入ボタンをクリックした場合
     */
    onBuyButonClicked: ({item}) => {

      // エラーメッセージが表示されていればクリアする
      group1.error_productsLicenses = ''

      CommonNewApplication.onBuyButonClicked(group1, {item})
    },

    /**
     * ライセンスのラジオボタンをプルダウン表示
     */
    onMouseEnterLicenseSelectItem: (item) => {
      if (store.state.common.device === 'sp') {
        return 
      }
      item.display_licenses_select = true
    },
    onMouseLeaveLicenseSelectItem: (item) => {
      if (store.state.common.device === 'sp') {
        return 
      }
      item.display_licenses_select = false
    },
    onClickLicenseSelectItem: (item) => {
      if (store.state.common.device === 'pc') {
        return 
      }
      item.display_licenses_select = true
    },
    
    /**
     * ライセンス数の選択肢をクリックした場合
     */
    onLicensesChoicesChanged: ({item, licenses}) => {
      CommonNewApplication.onLicensesChoicesChanged(group1, {item, licenses})

      console.log('[onLicensesChoicesChanged]', { item, licenses })
    },

    /**
     * リセットボタン（消しゴムボタン）をクリックした場合
     */
    onResetButtonClicked: ({item}) => {
      CommonNewApplication.onResetButtonClicked(group1, {item})

      console.log('[onResetButtonClicked]', item)
    },

    /**
     * 申込認証コード取得
     */
    getOfferCode: () => {
      CommonNewApplication.onGetOfferCode(group1)
    },

    /**
     * ログインボタン押下時
     */
    onWebLoginClicked: () => {
      group1.updateStore()
    },

    /**
     * 新規契約申込ボタン押下時
     */
    onWebLoginCheckClicked: () => {
      CommonNewApplication.getWebSession(group1).finally(() => {
        const isWebAccount = group1.isWebSessionFetched && group1.isWebSessionValid
        group1.isLoginChecked = true

        // WEB会員ログイン後の場合のみ更新する。
        if (isWebAccount) {
          group1.updateStore()

          router.push({ path: '/pc/new-application-input' })

          Vue.nextTick(() => {
            document.getElementById('payment-scroll').scrollIntoView()
          })
        }
      })
    },

    /**
     * 同意した上で進むボタンを押下した時
     */
    onSubmitButtonClicked: () => {
      group1.updateProductsLicenses()

      // 入力チェック
      const actionAfterCompletion = (group1, isError) => {
        CommonNewApplication.onGetOfferCode(group1).then(() => {
          if (group1.error_offerCode) {
            isError = true
          }
        }).finally(() => {
          // 選択した商品
          if (group1.productsLicenses.length === 0) {
            group1.error_productsLicenses = 'ご契約を希望する「パッケージ/商品」の新規契約ボタンが押下されていません。'
            isError = true
          }

          group1.isError = isError

          if (group1.isError) {
            // 入力エラーが存在した場合

            // 画面の最上部にスクロールする
            // 画面内にスクロールのある要素が入っている形なので、そのスクロール内の最上部に移動する処理とする。
            document.getElementById('contact-scroll').scrollTop = 0

            return false
          }

          group1.updateStore()

          // 確認画面に遷移
          if (store.state.common.device === 'sp') {
            router.push({ path: '/sp/new-application-confirm' })
          } else {
            router.push({ path: '/pc/new-application-confirm' })
          }
        })
      }
      CommonInquiry.checkInput(group1, actionAfterCompletion)
    },

    /**
     * ご請求金額合計
     */
    calcTotalPrice: () => {
      return CommonNewApplication.calcTotalPrice(group1)
    },

    /**
     * ご請求金額合計（表示用）
     */
    totalFreeDisp: computed(() => {
      return common.breakComma(group1.calcTotalPrice())
    }),

    /**
     * 見積もりを希望するパッケージのデータを「商品IDと購入ライセンス数のリスト」に整形
     */
    updateProductsLicenses: () => {
      const productsLicensesWork = []
      for (let i = 0; i < group1.packagesAndProducts.length; i++) {
        if (group1.packagesAndProducts[i].is_bought && group1.packagesAndProducts[i].licenses > 0) {
          const map = {
            id: group1.packagesAndProducts[i].product_id,
            licenses: group1.packagesAndProducts[i].licenses,
  
            package_id: group1.packagesAndProducts[i].package_id,
            package_name: group1.packagesAndProducts[i].package_name,
            product_id: group1.packagesAndProducts[i].product_id,
            product_name: group1.packagesAndProducts[i].product_name,
            classification: group1.packagesAndProducts[i].classification,
            price: group1.packagesAndProducts[i].price
          }
          productsLicensesWork.push(map)
        }
      }
      group1.productsLicenses.length = 0
      group1.productsLicenses.push(...productsLicensesWork)
    },

    /**
     * storeを更新
     */
    updateStore: () => {
      // 入力値
      const inputParams = {
        introducer: group1.introducer,
        note: group1.note,
        productsLicenses: group1.productsLicenses,
        radioPaymentMethod: group1.radioPaymentMethod
      }

      // ストアに入力値を保持
      store.dispatch('inquiry/setFormInputValues', inputParams)
      store.dispatch('newApplication/setTotalFee', group1.calcTotalPrice())
    }
  })

  return group1
}

// 新規契約申し込み確認画面（LCログイン後）
export const getGroupConfirm = () => {

  const group1 = reactive({
    // webSessionを取得したかどうか
    isWebSessionFetched: false,
    // ログイン済みかどうか
    isWebSessionValid: false,

    // 表示項目
    introducer: '',
    note: '',
    noteDisp: computed(() => {
      let value = group1.note
      value = common.escapeHTML(value)
      return value.replace(/\r?\n/g, '<br>')
    }),
    productsLicensesDisp: [], // 選択した商品情報のリスト（選択パッケージ＆ライセンス情報一覧APIの値を格納し表示する）
    packageLists: [], // 選択した商品情報のリスト

    totalFee: 0, // ご請求金額 合計
    totalFeeDisp: '-', // ご請求金額 合計（表示用）
    paymentMethod: PAYMENT_METHOD.CREDIT_CARD_LUMP_SUM, // 決済方法
    paymentName: PAYMENT_METHOD_NAME[PAYMENT_METHOD.CREDIT_CARD_LUMP_SUM], // 決済方法名
    offerCode: '', // 申込認証コード

    currentDate: new Date(), // アクセス時の日付
    licensePeriodStart: computed(() => { // ライセンス期間（表示用） 翌月1日
      const startDate = new Date(group1.currentDate.getTime())
      // まずは当月の1日を設定し、その後その日の翌月を取得する。
      startDate.setDate(1)
      startDate.setMonth(startDate.getMonth() + 1)
      return `${startDate.getFullYear()}年${startDate.getMonth() + 1}月${startDate.getDate()}日`
    }),
    licensePeriodEnd: computed(() => { // ライセンス期間（表示用） 翌月1日+1年-1日
      const endDate = new Date(group1.currentDate.getTime())
      // まずは当月の1日を設定し、その後その日の翌月を取得する。
      endDate.setDate(1)
      endDate.setMonth(endDate.getMonth() + 1)
      // 更にその一年後-1日を取得する。
      endDate.setFullYear(endDate.getFullYear() + 1)
      endDate.setDate(endDate.getDate() - 1)
      return `${endDate.getFullYear()}年${endDate.getMonth() + 1}月${endDate.getDate()}日`
    }),

    // パッケージ・商品一覧ロード完了フラグ
    isPackageAndLicensesLoaded: false,

    /**
     * 初期表示
     */
    init: () => {
      // WEB会員ログインチェック
      store.dispatch('common/showLoading')
      CommonNewApplication.getWebSession(group1).finally(() => {
        store.dispatch('common/hideLoading')

        const isWebAccount = group1.isWebSessionFetched && group1.isWebSessionValid

        // WEB会員ログイン前
        if (!isWebAccount) {
          if (store.state.common.device === 'sp') {
            router.push({ path: '/sp/new-application-input' })
          } else {
            router.push({ path: '/pc/new-application-input' })
          }
          return
        }

        // ログイン後
        group1.currentDate = new Date()
        // ストアからデータをロード
        group1.introducer = store.state.inquiry.introducer ?? ''
        group1.note = store.state.inquiry.note ?? ''
        group1.productsLicenses = store.state.inquiry.productsLicenses ?? []
        group1.totalFee = store.state.newApplication.totalFee
        group1.totalFeeDisp = common.breakComma(group1.totalFee)
        group1.paymentMethod = store.state.newApplication.paymentMethod
        group1.paymentName = PAYMENT_METHOD_NAME[group1.paymentMethod]
        group1.offerCode = store.state.newApplication.offerCode

        const packageList = []
        for (const product of group1.productsLicenses) {
          const productItem = {
            name: product.product_name,
            license_number: product.licenses,
            price: product.price,
            price_disp: common.breakComma(product.price)
          }
          const foundPackage = packageList.find(pkg => (pkg.package_id === product.package_id))
          if (foundPackage) {
            foundPackage.sub_total_fee += product.price
            foundPackage.sub_total_fee_disp = common.breakComma(foundPackage.sub_total_fee)
            if (product.classification === 2) {
              foundPackage.basic_products.push(productItem)
            } else if (product.classification === 3) {
              foundPackage.option_products.push(productItem)
            }
          } else {
            packageList.push({
              package_id: product.package_id,
              package_name: product.package_name,
              sub_total_fee: product.price,
              sub_total_fee_disp: common.breakComma(product.price),
              basic_products: (product.classification === 2) ? [productItem] : [],
              option_products: (product.classification === 3) ? [productItem] : []
            })
          }
        }

        group1.productsLicensesDisp = packageList
      })
    },

    /**
     * 確定ボタンクリック時
     * TODO: ロジック改修
     */
    onSubmitButtonClicked: () => {
      // 二重送信防止
      if (group1.isProcessing) {
        const payload = {
          title: '二重送信エラー',
          text: '短い時間に複数回送信ボタンが押されました。\n二重送信の可能性があるため、2度目以降の処理を中断します。'
        }
        store.dispatch('common/showNotice', payload)
        return
      }

      // 送信中フラグ（二重送信防止用）をtrueに設定
      group1.isProcessing = true

      // 画面リロードにより入力データが消えた場合に登録できないようにする。
      // 入力チェック
      const actionAfterCompletion = (group1, isError) => {
        // 選択した商品
        if (group1.productsLicenses.length === 0) {
          isError = true
        }
        if (isError) {
          // 入力エラーが存在した場合
          store.dispatch('common/showNotice', { title: 'エラー', text: '入力データが設定されていません。' })
          return false
        }

        const params = {
          name: store.state.profile.name,
          payment_classification: group1.paymentMethod,
          request_code: group1.offerCode,
          webuser_id: group1.web_mem_info.webuser_id,
          introducer: group1.introducer,
          products_licenses: group1.productsLicenses.map(({ id, licenses }) => ({ id, licenses })), // 確認画面用にプロパティを追加しているので、id, licensesのみにする
          note: group1.note,
          identification_code: store.state.profile.identification_code,
          agreement_id: store.state.profile.agreement_id,
          user_id: store.state.profile.id,
          email: store.state.profile.email
        }
        console.log(params)
        apiInquiry.completeLoggedinNewApplication(params)
          .then((response) => {
            if (!response) {
              return
            }

            const { data } = response
            // const data = {
            //   status: 0,
            //   error_message: ''
            // }
            if (data.status === 0) {
              // 成功の場合
              // 完了画面に遷移
              if (store.state.common.device === 'sp') {
                router.push({ path: '/sp/new-application-complete' })
              } else {
                router.push({ path: '/pc/new-application-complete' })
              }
              return
            }

            // エラーの場合
            store.dispatch('common/showNotice', { title: 'エラー', text: data.error_message })
          }).finally(() => {
            // 送信中フラグ（二重送信防止用）をfalseに設定
            group1.isProcessing = false
            store.dispatch('common/hideLoading')
          })
      }
      CommonInquiry.checkInput(group1, actionAfterCompletion)
    }
  })

  return group1
}

// 新規契約申し込み完了画面（LCログイン後）
export const getGroupComplete = () => {

  const group1 = reactive({
    // webSessionを取得したかどうか
    isWebSessionFetched: false,
    // ログイン済みかどうか
    isWebSessionValid: false,

    /**
     * 初期表示
     */
    init: () => {
      // WEB会員ログインチェック
      store.dispatch('common/showLoading')
      CommonNewApplication.getWebSession(group1).finally(() => {
        store.dispatch('common/hideLoading')

        const isWebAccount = group1.isWebSessionFetched && group1.isWebSessionValid

        // WEB会員ログイン前
        if (!isWebAccount) {
          if (store.state.common.device === 'sp') {
            router.push({ path: '/sp/new-application-input' })
          } else {
            router.push({ path: '/pc/new-application-input' })
          }
          return
        }

        // ストアに保持している値を全クリア
        store.dispatch('inquiry/clearAllStates')
        store.dispatch('newApplication/clearAllStates')
      })
    }
  })

  return group1
}
